// Operation and Action validation constants and helpers

// Operator types
export const ARITHMETIC_OPERATORS = [0, 1, 2, 3, 4, 14, 17, 18]; // None, Add, Subtract, Multiply, Divide, Delay, Delta, Map
export const COMPARISON_OPERATORS = [5, 6, 7, 8, 9, 10, 15, 16];

// Action types
export const ACTION_TYPES = {
    EMAIL: '1',
    ADD_TO_DASHBOARD: '2',
    SMS: '3',
    WHATSAPP: '4',
    MQTT: '5',
    API: '6'
};

// Actions that require a comparison condition (can cause spam if not validated)
export const NOTIFICATION_ACTIONS = [ACTION_TYPES.EMAIL, ACTION_TYPES.SMS, ACTION_TYPES.WHATSAPP, ACTION_TYPES.MQTT, ACTION_TYPES.API];

// Operator names — values are i18n translation keys (use t(OPERATOR_NAMES[x]) to display)
export const OPERATOR_NAMES = {
    '0': 'operations.operators.none',
    '1': 'operations.operators.add',
    '2': 'operations.operators.subtract',
    '3': 'operations.operators.multiply',
    '4': 'operations.operators.divide',
    '5': 'operations.operators.lessThan',
    '6': 'operations.operators.greaterThan',
    '7': 'operations.operators.lessOrEqual',
    '8': 'operations.operators.greaterOrEqual',
    '9': 'operations.operators.equal',
    '10': 'operations.operators.notEqual',
    '14': 'operations.operators.delay',
    '15': 'operations.operators.timer',
    '16': 'operations.operators.not',
    '17': 'operations.operators.delta',
    '18': 'operations.operators.map'
};

// Action type names — values are i18n translation keys (use t(ACTION_TYPE_NAMES[x]) to display)
export const ACTION_TYPE_NAMES = {
    '1': 'operations.actionTypes.email',
    '2': 'operations.actionTypes.dashboard',
    '3': 'operations.actionTypes.sms',
    '4': 'operations.actionTypes.whatsapp',
    '5': 'operations.actionTypes.mqtt',
    '6': 'operations.actionTypes.api'
};

/**
 * Check if an operator is arithmetic (cannot trigger notifications alone)
 */
export function isArithmeticOperator(operator) {
    return ARITHMETIC_OPERATORS.includes(parseInt(operator));
}

/**
 * Check if an operator is a comparison (can trigger notifications)
 */
export function isComparisonOperator(operator) {
    return COMPARISON_OPERATORS.includes(parseInt(operator));
}

/**
 * Check if an action type requires a comparison condition
 */
export function requiresCondition(actionType) {
    return NOTIFICATION_ACTIONS.includes(actionType);
}

/**
 * Get all operations connected to an action node
 * @param {string} actionNodeId - The action node ID
 * @param {Array} edges - All edges in the flow
 * @param {Array} nodes - All nodes in the flow
 * @returns {Array} - Array of connected operation nodes
 */
export function getConnectedOperations(actionNodeId, edges, nodes) {
    console.log('Looking for edges connected to action:', actionNodeId);
    console.log('All edges:', edges.map(e => ({ source: e.source, target: e.target, targetHandle: e.targetHandle })));

    // Check for edges where target matches the action node ID
    // The target could be just the node ID or include the handle
    const connectedEdges = edges.filter(edge => {
        const targetMatches = edge.target === actionNodeId ||
                              edge.target === String(actionNodeId) ||
                              edge.target?.startsWith?.(actionNodeId);
        return targetMatches;
    });

    console.log('Found connected edges:', connectedEdges);

    const connectedOperations = [];

    connectedEdges.forEach(edge => {
        const sourceNode = nodes.find(node => node.id === edge.source || String(node.id) === edge.source);
        console.log('Looking for source node:', edge.source, 'found:', sourceNode?.id, sourceNode?.type);
        if (sourceNode && (sourceNode.type === 'operation' || sourceNode.type === 'timer' || sourceNode.type === 'delay')) {
            connectedOperations.push(sourceNode);
        }
    });

    return connectedOperations;
}

/**
 * Get the operator value from an operation node, handling different data structures
 */
function getOperatorFromNode(op) {
    // Try different paths where the operator might be stored
    let operator = op.data?.operation ?? op.operation ?? op.data?.operator ?? op.operator;

    // Convert to number for comparison
    if (operator !== undefined && operator !== null) {
        return parseInt(String(operator), 10);
    }
    return null;
}

/**
 * Validate an action node against business rules
 * @param {Object} actionNode - The action node to validate
 * @param {Array} connectedOperations - Operations connected to this action
 * @returns {Object} - { valid: boolean, message: string, type: 'error' | 'warning' }
 */
export function validateAction(actionNode, connectedOperations) {
    const actionType = actionNode.data?.action_type || actionNode.action_type;

    // Add to Dashboard - doesn't require conditions
    if (actionType === ACTION_TYPES.ADD_TO_DASHBOARD) {
        return { valid: true, message: '', type: null };
    }

    // Email, SMS, WhatsApp - require comparison conditions
    if (NOTIFICATION_ACTIONS.includes(actionType)) {
        if (connectedOperations.length === 0) {
            return {
                valid: false,
                message: 'This action must be connected to a comparison condition to prevent spam. Connect an operation with an operator like >, <, =, etc.',
                type: 'error'
            };
        }

        // Check if any connected operation is a comparison operator
        const hasComparison = connectedOperations.some(op => {
            const operator = getOperatorFromNode(op);
            return isComparisonOperator(operator);
        });

        if (hasComparison) {
            return { valid: true, message: '', type: null };
        }

        // If no comparison found, check if all are arithmetic
        const hasOnlyArithmetic = connectedOperations.every(op => {
            const operator = getOperatorFromNode(op);
            return isArithmeticOperator(operator);
        });

        if (hasOnlyArithmetic) {
            return {
                valid: false,
                message: 'Arithmetic operators (+, -, x, ÷) cannot trigger notifications by themselves. Use a comparison operator like "Greater than" (>) or "Equal to" (=) to define when the notification should be sent.',
                type: 'error'
            };
        }

        // If we get here, there's some unknown operator type - allow it for safety
        return { valid: true, message: '', type: null };
    }

    return { valid: true, message: '', type: null };
}

/**
 * Validate all action nodes in the flow before saving
 * @param {Array} nodes - All nodes in the flow
 * @param {Array} edges - All edges in the flow
 * @returns {Object} - { valid: boolean, errors: Array, warnings: Array }
 */
export function validateAllActions(nodes, edges) {
    const errors = [];
    const warnings = [];

    const actionNodes = nodes.filter(node => node.type === 'action');

    actionNodes.forEach(actionNode => {
        const connectedOperations = getConnectedOperations(actionNode.id, edges, nodes);
        const validation = validateAction(actionNode, connectedOperations);

        if (!validation.valid) {
            const actionTypeName = ACTION_TYPE_NAMES[actionNode.data?.action_type] || 'Action';
            if (validation.type === 'error') {
                errors.push({
                    nodeId: actionNode.id,
                    actionType: actionTypeName,
                    message: validation.message
                });
            } else if (validation.type === 'warning') {
                warnings.push({
                    nodeId: actionNode.id,
                    actionType: actionTypeName,
                    message: validation.message
                });
            }
        }
    });

    return {
        valid: errors.length === 0,
        errors,
        warnings
    };
}

/**
 * Get help text for action types
 */
export const ACTION_HELP_TEXT = {
    [ACTION_TYPES.EMAIL]: {
        title: 'Email Notification',
        description: 'Notifications are sent when a specific condition is met.',
        warning: 'ANTI-SPAM PROTECTION: The notification will be sent ONCE when the condition is met. It will only be sent again after the condition becomes false and then true again.',
        requirement: 'Requires a comparison condition (>, <, =, etc.)'
    },
    [ACTION_TYPES.SMS]: {
        title: 'SMS Notification',
        description: 'SMS notifications are sent when a specific condition is met.',
        warning: 'ANTI-SPAM PROTECTION: The SMS will be sent ONCE when the condition is met. It will only be sent again after the condition becomes false and then true again.',
        requirement: 'Requires a comparison condition (>, <, =, etc.)'
    },
    [ACTION_TYPES.WHATSAPP]: {
        title: 'WhatsApp Notification',
        description: 'WhatsApp messages are sent when a specific condition is met.',
        warning: 'ANTI-SPAM PROTECTION: The message will be sent ONCE when the condition is met. It will only be sent again after the condition becomes false and then true again.',
        requirement: 'Requires a comparison condition (>, <, =, etc.)'
    },
    [ACTION_TYPES.ADD_TO_DASHBOARD]: {
        title: 'Add to Dashboard',
        description: 'Adds a widget to the dashboard with the value of a variable or operation result.',
        warning: null,
        requirement: null
    },
    [ACTION_TYPES.MQTT]: {
        title: 'Send MQTT Message',
        description: 'Sends a message to an MQTT broker when a condition is met.',
        warning: 'ANTI-SPAM PROTECTION: The message will be sent ONCE when the condition is met. It will only be sent again after the condition becomes false and then true again.',
        requirement: 'Requires a comparison condition (>, <, =, etc.)'
    },
    [ACTION_TYPES.API]: {
        title: 'API Call',
        description: 'Makes an HTTP API call when a condition is met.',
        warning: 'ANTI-SPAM PROTECTION: The API call will be made ONCE when the condition is met. It will only be made again after the condition becomes false and then true again.',
        requirement: 'Requires a comparison condition (>, <, =, etc.)'
    }
};
